import { useEffect, useState } from "react";
import { api, formatTL, resolveImage, DELIVERY_PLATFORMS, platformPrice } from "@/lib/api";
import { printReceipt } from "@/lib/receipt";
import { RemovablesModal } from "@/components/app/RemovablesModal";
import { Plus, Minus, Trash2, Search, Truck, UserPlus, Check, X, Banknote, CreditCard, Wallet } from "lucide-react";
import { toast } from "sonner";

const TAX_RATE = 0.1;

const PAYMENTS = [
  { id: "Kapıda Nakit", label: "Kapıda Nakit", icon: Banknote },
  { id: "Kapıda Kredi Kartı", label: "Kapıda Kredi Kartı", icon: CreditCard },
  { id: "Online Ödendi", label: "Online Ödendi", icon: Wallet },
];

const PLATFORM_OPTIONS = [{ key: "standart", label: "Telefon / Standart" }, ...DELIVERY_PLATFORMS];

export const DeliveryOrderPanel = ({ onPlaced }) => {
  const [products, setProducts] = useState([]);
  const [cats, setCats] = useState(["Tüm Ürünler"]);
  const [activeCat, setActiveCat] = useState("Tüm Ürünler");
  const [platform, setPlatform] = useState("standart");
  const [cart, setCart] = useState([]);
  const [customer, setCustomer] = useState({ name: "", phone: "", address: "" });
  const [payment, setPayment] = useState(PAYMENTS[0].id);
  const [placing, setPlacing] = useState(false);
  const [q, setQ] = useState("");
  const [custQuery, setCustQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [searchingCust, setSearchingCust] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [removalProduct, setRemovalProduct] = useState(null);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [addForm, setAddForm] = useState({ name: "", phone: "", address: "", note: "" });
  const [savingNew, setSavingNew] = useState(false);

  useEffect(() => {
    Promise.all([api.get("/products"), api.get("/categories")])
      .then(([pr, cr]) => {
        setProducts(pr.data.filter((p) => p.active !== false));
        setCats(["Tüm Ürünler", ...cr.data.map((x) => x.name)]);
      })
      .catch(() => toast.error("Menü yüklenemedi"));
  }, []);

  // Platform değişince sepetteki taban fiyatlar yeniden hesaplanır (ekstra/içecek korunur)
  useEffect(() => {
    setCart((prev) =>
      prev.map((i) => {
        const prod = products.find((p) => p.id === i.product_id);
        return prod ? { ...i, price: platformPrice(prod, platform) + (i.extra || 0) } : i;
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [platform]);

  // Kioskdaki gibi: çıkarılan/eklenen malzeme + içecek seçimiyle sepete ekle
  const addItemCustom = (product, removals = [], added = [], drink = null) => {
    const parts = [];
    if (removals.length) parts.push(`Çıkarılan: ${removals.join(", ")}`);
    if (added.length) parts.push(`Eklenen: ${added.map((a) => a.name).join(", ")}`);
    if (drink) parts.push(`İçecek: ${drink.name}`);
    const note = parts.join(" · ");
    const extra = added.reduce((s, a) => s + (a.price || 0), 0) + (drink ? drink.price || 0 : 0);
    const price = platformPrice(product, platform) + extra;
    const uid = `${product.id}::${note}`;
    setCart((prev) => {
      const f = prev.find((i) => i.uid === uid);
      if (f) return prev.map((i) => (i.uid === uid ? { ...i, quantity: i.quantity + 1 } : i));
      return [...prev, { uid, product_id: product.id, name: product.name, price, extra, quantity: 1, note }];
    });
  };

  // Ürünün içecek/çıkarılabilir/eklenebilir seçeneği varsa modal aç, yoksa direkt ekle
  const handleAddRequest = (p) => {
    const hasOpts =
      (p.removables && p.removables.length) ||
      (p.extras && p.extras.length) ||
      (p.drinks && p.drinks.length);
    if (hasOpts) setRemovalProduct(p);
    else addItemCustom(p);
  };

  const changeQty = (uid, d) =>
    setCart((prev) =>
      prev
        .map((i) => (i.uid === uid ? { ...i, quantity: i.quantity + d } : i))
        .filter((i) => i.quantity > 0)
    );

  // Telefon/ad yazınca kayıtlı müşteri ara
  useEffect(() => {
    const term = custQuery.trim();
    if (term.length < 2) {
      setSuggestions([]);
      return;
    }
    setSearchingCust(true);
    const t = setTimeout(async () => {
      try {
        const res = await api.get("/customers", { params: { search: term } });
        setSuggestions(res.data);
      } catch (e) {
        setSuggestions([]);
      }
      setSearchingCust(false);
    }, 300);
    return () => clearTimeout(t);
  }, [custQuery]);

  const selectCustomer = (c) => {
    setCustomer({ name: c.name, phone: c.phone, address: c.address });
    setSelectedId(c.id);
    setCustQuery("");
    setSuggestions([]);
  };

  const openAddModal = () => {
    const term = custQuery.trim();
    const looksPhone = /^[0-9+()\s-]+$/.test(term);
    setAddForm({
      name: customer.name || (looksPhone ? "" : term),
      phone: customer.phone || (looksPhone ? term : ""),
      address: customer.address || "",
      note: "",
    });
    setAddModalOpen(true);
  };

  const submitNewCustomer = async (e) => {
    e.preventDefault();
    if (!addForm.name.trim() || !addForm.phone.trim()) {
      toast.error("Ad ve telefon zorunludur");
      return;
    }
    if (addForm.phone.replace(/\D/g, "").length !== 10) {
      toast.error("Telefon numarası 10 haneli olmalıdır");
      return;
    }
    setSavingNew(true);
    try {
      const res = await api.post("/customers", {
        name: addForm.name,
        phone: addForm.phone,
        address: addForm.address,
        note: addForm.note,
      });
      setCustomer({ name: res.data.name, phone: res.data.phone, address: res.data.address });
      setSelectedId(res.data.id);
      setCustQuery("");
      setSuggestions([]);
      setAddModalOpen(false);
      toast.success("Yeni müşteri eklendi ve siparişe seçildi");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Müşteri eklenemedi");
    }
    setSavingNew(false);
  };

  const total = cart.reduce((s, i) => s + i.price * i.quantity, 0);
  const tax = total - total / (1 + TAX_RATE);
  const subtotal = total - tax;

  const placeOrder = async () => {
    if (cart.length === 0) return toast.error("Sepete ürün ekleyin");
    if (!customer.name.trim() || !customer.phone.trim() || !customer.address.trim())
      return toast.error("Ad soyad, telefon ve adres zorunludur");
    if (customer.phone.replace(/\D/g, "").length !== 10)
      return toast.error("Telefon numarası 10 haneli olmalıdır");
    setPlacing(true);
    try {
      const res = await api.post("/orders", {
        items: cart,
        order_type: "Paket Servis",
        payment_method: payment,
        subtotal: Number(subtotal.toFixed(2)),
        tax: Number(tax.toFixed(2)),
        total: Number(total.toFixed(2)),
        customer_name: customer.name,
        customer_phone: customer.phone,
        customer_address: customer.address,
        platform: PLATFORM_OPTIONS.find((o) => o.key === platform)?.label || "",
      });
      printReceipt(res.data, "both");
      toast.success(`Paket sipariş oluşturuldu · #${res.data.order_number}`);
      setCart([]);
      setCustomer({ name: "", phone: "", address: "" });
      setSelectedId(null);
      setCustQuery("");
      onPlaced?.();
    } catch (e) {
      toast.error("Sipariş oluşturulamadı");
    }
    setPlacing(false);
  };

  const filtered = products.filter(
    (p) =>
      (activeCat === "Tüm Ürünler" || p.category === activeCat) &&
      p.name.toLowerCase().includes(q.toLowerCase())
  );

  return (
    <div className="grid grid-cols-1 lg:grid-cols-5 gap-5">
      {/* Ürünler */}
      <div className="lg:col-span-3">
        <div className="mb-4 flex flex-wrap gap-2" data-testid="delivery-platform-selector">
          {PLATFORM_OPTIONS.map((o) => (
            <button
              key={o.key}
              data-testid={`delivery-platform-${o.key}`}
              onClick={() => setPlatform(o.key)}
              className={`px-3 py-1.5 rounded-full text-sm font-semibold border transition-colors ${
                platform === o.key
                  ? "bg-[var(--fb-red)] text-white border-[var(--fb-red)]"
                  : "bg-white text-[var(--fb-muted)] border-[var(--fb-border)] hover:border-[var(--fb-red)] hover:text-[var(--fb-red)]"
              }`}
            >
              {o.label}
            </button>
          ))}
        </div>
        <div className="mb-3 flex flex-wrap gap-2" data-testid="delivery-category-selector">
          {cats.map((cat) => (
            <button
              key={cat}
              data-testid={`delivery-cat-${cat}`}
              onClick={() => setActiveCat(cat)}
              className={`px-3 py-1.5 rounded-full text-sm font-semibold border transition-colors ${
                activeCat === cat
                  ? "bg-[var(--fb-ink)] text-white border-[var(--fb-ink)]"
                  : "bg-white text-[var(--fb-muted)] border-[var(--fb-border)] hover:border-[var(--fb-ink)] hover:text-[var(--fb-ink)]"
              }`}
            >
              {cat}
            </button>
          ))}
        </div>
        <div className="relative mb-4">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[var(--fb-muted)]" />
          <input
            data-testid="delivery-product-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Ürün ara..."
            className="w-full bg-white border border-[var(--fb-border)] rounded-full pl-9 pr-4 py-2 text-sm outline-none focus:border-[var(--fb-red)]"
          />
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {filtered.map((p) => (
            <button
              key={p.id}
              data-testid={`delivery-add-${p.id}`}
              onClick={() => handleAddRequest(p)}
              className="bg-white rounded-xl border border-[var(--fb-border)] overflow-hidden text-left hover:border-[var(--fb-red)] hover:shadow transition-all active:scale-95"
            >
              <div className="aspect-[4/3] bg-[var(--fb-neutral)] overflow-hidden">
                {p.image_url && (
                  <img src={resolveImage(p.image_url)} alt={p.name} className="w-full h-full object-cover" />
                )}
              </div>
              <div className="p-2.5">
                <p className="font-semibold text-sm truncate">{p.name}</p>
                <p className="text-[var(--fb-red)] font-bold text-sm">
                  {formatTL(platformPrice(p, platform))}
                </p>
                {((p.drinks && p.drinks.length) || (p.removables && p.removables.length) || (p.extras && p.extras.length)) && (
                  <p className="text-[10px] text-[var(--fb-muted)] mt-0.5">Seçenekli · dokunun</p>
                )}
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Sipariş özeti + müşteri */}
      <div className="lg:col-span-2">
        <div className="bg-white rounded-2xl border border-[var(--fb-border)] p-4 space-y-4 lg:sticky lg:top-24">
          <h3 className="font-display font-bold flex items-center gap-2">
            <Truck className="w-5 h-5 text-[var(--fb-red)]" /> Paket Servis Siparişi
          </h3>

          {/* Müşteri bilgileri */}
          <div className="space-y-2">
            <input
              data-testid="delivery-customer-phone"
              value={customer.phone}
              inputMode="numeric"
              maxLength={10}
              onChange={(e) => {
                const v = e.target.value.replace(/\D/g, "").slice(0, 10);
                setCustomer((c) => ({ ...c, phone: v }));
                setCustQuery(v);
                setSelectedId(null);
              }}
              placeholder="Telefon (yazınca kayıtlı müşteri aranır)"
              className="w-full text-sm border border-[var(--fb-border)] rounded-lg px-3 py-2 outline-none focus:border-[var(--fb-red)]"
            />
            <input
              data-testid="delivery-customer-name"
              value={customer.name}
              onChange={(e) => {
                setCustomer((c) => ({ ...c, name: e.target.value }));
                setCustQuery(e.target.value);
                setSelectedId(null);
              }}
              placeholder="Ad Soyad"
              className="w-full text-sm border border-[var(--fb-border)] rounded-lg px-3 py-2 outline-none focus:border-[var(--fb-red)]"
            />

            {custQuery.trim().length >= 2 && !selectedId && (
              <div
                data-testid="delivery-customer-suggestions"
                className="border border-[var(--fb-border)] rounded-lg overflow-hidden divide-y divide-[var(--fb-border)]"
              >
                {searchingCust ? (
                  <div className="px-3 py-2 text-xs text-[var(--fb-muted)]">Aranıyor...</div>
                ) : suggestions.length > 0 ? (
                  suggestions.slice(0, 5).map((c) => (
                    <button
                      key={c.id}
                      type="button"
                      data-testid={`delivery-suggest-${c.id}`}
                      onClick={() => selectCustomer(c)}
                      className="w-full text-left px-3 py-2 hover:bg-[var(--fb-offwhite)] transition-colors"
                    >
                      <span className="font-semibold text-sm">{c.name}</span>
                      <span className="text-xs text-[var(--fb-muted)] ml-2">{c.phone}</span>
                      {c.address && (
                        <div className="text-xs text-[var(--fb-muted)] truncate">{c.address}</div>
                      )}
                    </button>
                  ))
                ) : (
                  <button
                    type="button"
                    data-testid="delivery-add-new-customer-btn"
                    onClick={openAddModal}
                    className="w-full flex items-center gap-2 px-3 py-2.5 text-sm font-semibold text-[var(--fb-red)] hover:bg-red-50 transition-colors"
                  >
                    <UserPlus className="w-4 h-4" /> Kayıtlı değil — Yeni müşteri ekle
                  </button>
                )}
              </div>
            )}
            {selectedId && (
              <div className="flex items-center gap-1.5 text-xs font-semibold text-green-600">
                <Check className="w-3.5 h-3.5" /> Kayıtlı müşteri seçildi
              </div>
            )}

            <textarea
              data-testid="delivery-customer-address"
              value={customer.address}
              onChange={(e) => setCustomer((c) => ({ ...c, address: e.target.value }))}
              placeholder="Teslimat adresi"
              rows={2}
              className="w-full text-sm border border-[var(--fb-border)] rounded-lg px-3 py-2 outline-none focus:border-[var(--fb-red)] resize-none"
            />
          </div>

          {/* Sepet */}
          <div className="border-t border-[var(--fb-border)] pt-3 space-y-2 max-h-52 overflow-y-auto fb-scroll">
            {cart.length === 0 ? (
              <p className="text-sm text-[var(--fb-muted)] text-center py-4">
                Soldan ürün ekleyin
              </p>
            ) : (
              cart.map((i) => (
                <div key={i.uid} className="flex items-start justify-between gap-2 text-sm">
                  <div className="flex-1 min-w-0">
                    <span className="block truncate">{i.name}</span>
                    {i.note && (
                      <span className="block text-xs text-[var(--fb-muted)] truncate">{i.note}</span>
                    )}
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      data-testid={`delivery-dec-${i.uid}`}
                      onClick={() => changeQty(i.uid, -1)}
                      className="w-6 h-6 grid place-items-center rounded-full hover:bg-[var(--fb-neutral)]"
                    >
                      <Minus className="w-3.5 h-3.5" />
                    </button>
                    <span className="w-5 text-center font-bold">{i.quantity}</span>
                    <button
                      data-testid={`delivery-inc-${i.uid}`}
                      onClick={() => changeQty(i.uid, 1)}
                      className="w-6 h-6 grid place-items-center rounded-full hover:bg-[var(--fb-neutral)]"
                    >
                      <Plus className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  <span className="w-16 text-right font-semibold">
                    {formatTL(i.price * i.quantity)}
                  </span>
                </div>
              ))
            )}
          </div>

          {/* Ödeme */}
          <div className="grid grid-cols-3 gap-2" data-testid="delivery-payment">
            {PAYMENTS.map((p) => {
              const Icon = p.icon;
              return (
                <button
                  key={p.id}
                  type="button"
                  data-testid={`delivery-payment-${p.id}`}
                  onClick={() => setPayment(p.id)}
                  className={`flex flex-col items-center gap-1 py-3 px-1 rounded-xl border text-xs font-semibold transition-colors ${
                    payment === p.id
                      ? "border-[var(--fb-red)] bg-red-50 text-[var(--fb-red)]"
                      : "border-[var(--fb-border)] text-[var(--fb-muted)]"
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  <span className="text-center leading-tight">{p.label}</span>
                </button>
              );
            })}
          </div>

          {/* Toplam */}
          <div className="border-t border-[var(--fb-border)] pt-3 space-y-1 text-sm">
            <div className="flex justify-between text-[var(--fb-muted)]">
              <span>Ara Toplam (KDV hariç)</span>
              <span>{formatTL(subtotal)}</span>
            </div>
            <div className="flex justify-between text-[var(--fb-muted)]">
              <span>KDV (%10) dahil</span>
              <span>{formatTL(tax)}</span>
            </div>
            <div className="flex justify-between font-display font-extrabold text-lg">
              <span>Toplam</span>
              <span data-testid="delivery-total" className="text-[var(--fb-red)]">
                {formatTL(total)}
              </span>
            </div>
          </div>

          <button
            data-testid="delivery-place-order-btn"
            disabled={placing}
            onClick={placeOrder}
            className="w-full bg-[var(--fb-red)] hover:bg-[var(--fb-red-hover)] disabled:opacity-60 text-white font-bold py-3.5 rounded-full transition-colors active:scale-[0.99]"
          >
            {placing ? "Oluşturuluyor..." : "Paket Siparişi Oluştur"}
          </button>
        </div>
      </div>

      {/* Kioskdaki gibi ürün özelleştirme (içecek/çıkarılabilir/eklenebilir) */}
      {removalProduct && (
        <RemovablesModal
          product={removalProduct}
          onClose={() => setRemovalProduct(null)}
          onConfirm={({ removals, added, drink }) => {
            addItemCustom(removalProduct, removals, added, drink);
            setRemovalProduct(null);
          }}
        />
      )}

      {/* Yeni müşteri ekle modalı */}
      {addModalOpen && (
        <div className="fixed inset-0 z-[80] grid place-items-center p-4">
          <div onClick={() => setAddModalOpen(false)} className="absolute inset-0 bg-black/50" />
          <div
            data-testid="delivery-add-customer-modal"
            className="relative bg-white rounded-2xl w-full max-w-md animate-fade-up"
          >
            <header className="flex items-center justify-between px-5 h-16 border-b border-[var(--fb-border)]">
              <h2 className="font-display font-bold text-lg flex items-center gap-2">
                <UserPlus className="w-5 h-5 text-[var(--fb-red)]" /> Yeni Müşteri Ekle
              </h2>
              <button
                data-testid="delivery-close-add-customer-btn"
                onClick={() => setAddModalOpen(false)}
                className="w-9 h-9 grid place-items-center rounded-full hover:bg-[var(--fb-neutral)]"
              >
                <X className="w-5 h-5" />
              </button>
            </header>
            <form onSubmit={submitNewCustomer} className="p-5 space-y-3">
              <input
                data-testid="delivery-new-name"
                value={addForm.name}
                onChange={(e) => setAddForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="Ad Soyad *"
                className="w-full border border-[var(--fb-border)] rounded-xl px-3 py-2.5 outline-none focus:border-[var(--fb-red)]"
              />
              <input
                data-testid="delivery-new-phone"
                value={addForm.phone}
                inputMode="numeric"
                maxLength={10}
                onChange={(e) => setAddForm((f) => ({ ...f, phone: e.target.value.replace(/\D/g, "").slice(0, 10) }))}
                placeholder="Telefon * (10 haneli)"
                className="w-full border border-[var(--fb-border)] rounded-xl px-3 py-2.5 outline-none focus:border-[var(--fb-red)]"
              />
              <textarea
                data-testid="delivery-new-address"
                value={addForm.address}
                onChange={(e) => setAddForm((f) => ({ ...f, address: e.target.value }))}
                placeholder="Adres"
                rows={2}
                className="w-full border border-[var(--fb-border)] rounded-xl px-3 py-2.5 outline-none focus:border-[var(--fb-red)] resize-none"
              />
              <input
                data-testid="delivery-new-note"
                value={addForm.note}
                onChange={(e) => setAddForm((f) => ({ ...f, note: e.target.value }))}
                placeholder="Not (opsiyonel)"
                className="w-full border border-[var(--fb-border)] rounded-xl px-3 py-2.5 outline-none focus:border-[var(--fb-red)]"
              />
              <button
                data-testid="delivery-save-new-customer-btn"
                type="submit"
                disabled={savingNew}
                className="w-full bg-[var(--fb-red)] hover:bg-[var(--fb-red-hover)] disabled:opacity-60 text-white font-bold py-3 rounded-full transition-colors"
              >
                {savingNew ? "Kaydediliyor..." : "Müşteriyi Ekle ve Seç"}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
